import { RoundCommandError } from "./round-errors.js";
import {
  isRoundV3,
  type RoundSnapshot,
  type RoundSnapshotV3,
} from "./round-types.js";
import type {
  RoundCommandPayload,
  RoundCommandPayloadMap,
} from "./round-reducer.js";
import { selectHoleEntry, selectPlayerHole } from "./round-selectors.js";
import { validateHoleEntry } from "./round-v3-validation.js";
import { selectStrokeScoreLocked } from "../contest/contest-selectors.js";
import { freezeFinishedMatches } from "../contest/contest-state.js";

export function setHoleEntry(
  snapshot: RoundSnapshot,
  payload: RoundCommandPayload,
): RoundSnapshot {
  requireState(snapshot, "active", "record a hole");
  if (!isRoundV3(snapshot))
    throw new RoundCommandError(
      "Legacy rounds do not support versioned hole entries.",
    );
  if (!("targetId" in payload) || !("entry" in payload))
    throw new RoundCommandError("Hole entry payload is incomplete.");
  if (
    !snapshot.participants.some(
      (participant) => participant.id === payload.targetId,
    ) ||
    !snapshot.orderedHoleNumbers.includes(payload.holeNumber)
  )
    throw new RoundCommandError(
      "The player or physical hole is not in this round.",
    );
  const { hole } = selectPlayerHole(
    snapshot,
    payload.targetId,
    payload.holeNumber,
  );
  validateHoleEntry(
    payload.entry,
    snapshot.mainContest.format === "stableford",
    hole.par,
  );
  requireEditableScore(snapshot, {
    targetId: payload.targetId,
    holeNumber: payload.holeNumber,
    entry: payload.entry,
  });
  const entry = {
    status: payload.entry.status,
    strokes: payload.entry.strokes,
    putts: payload.entry.putts,
    fairwayHit: payload.entry.fairwayHit,
    greenInRegulation: payload.entry.greenInRegulation,
    source: payload.entry.source,
    revision:
      (snapshot.holeEntries[payload.targetId]?.[payload.holeNumber]?.revision ??
        0) + 1,
  };
  const next: RoundSnapshotV3 = {
    ...snapshot,
    version: snapshot.version + 1,
    holeEntries: {
      ...snapshot.holeEntries,
      [payload.targetId]: {
        ...snapshot.holeEntries[payload.targetId],
        [payload.holeNumber]: entry,
      },
    },
    scores: {
      ...snapshot.scores,
      [payload.targetId]: {
        ...snapshot.scores[payload.targetId],
        [payload.holeNumber]: entry.strokes,
      },
    },
  };
  return freezeFinishedMatches(next);
}

export function transferScorekeeper(
  snapshot: RoundSnapshot,
  payload: RoundCommandPayload,
): RoundSnapshot {
  requireState(snapshot, "active", "transfer scorekeeping");
  if (!isRoundV3(snapshot))
    throw new RoundCommandError(
      "Legacy rounds cannot transfer scorekeeping. Finish them with their original scorekeeper.",
    );
  if (
    !("nextScorekeeperId" in payload) ||
    !payload.nextScorekeeperId.trim() ||
    payload.nextScorekeeperId === snapshot.scorekeeperId ||
    payload.expectedAcknowledgedVersion !== snapshot.version
  )
    throw new RoundCommandError(
      "Transfer requires a different scorekeeper and the exact last acknowledged round version.",
    );
  return {
    ...snapshot,
    scorekeeperId: payload.nextScorekeeperId,
    scorekeeperGeneration: snapshot.scorekeeperGeneration + 1,
    version: snapshot.version + 1,
  };
}

function requireEditableScore(
  snapshot: RoundSnapshotV3,
  payload: RoundCommandPayloadMap["hole.entry.set"],
): void {
  if (selectStrokeScoreLocked(snapshot, payload.holeNumber)) {
    const old = selectHoleEntry(snapshot, payload.targetId, payload.holeNumber);
    if (
      old.status !== payload.entry.status ||
      old.strokes !== payload.entry.strokes
    )
      throw new RoundCommandError(
        "The decided match result is locked. Use the result correction process.",
      );
  }
}

function requireState(
  snapshot: RoundSnapshot,
  expected: "active",
  action: string,
): void {
  if (snapshot.state !== expected)
    throw new RoundCommandError(
      `Cannot ${action} a round in ${snapshot.state} state.`,
    );
}

export function startV3Round(
  snapshot: RoundSnapshotV3,
  occurredAt: string,
): RoundSnapshot {
  if (!Number.isFinite(Date.parse(occurredAt)))
    throw new RoundCommandError("The agreement time is invalid.");
  return {
    ...snapshot,
    state: "active",
    version: snapshot.version + 1,
    mainContest: { ...snapshot.mainContest, lockedAt: occurredAt },
    participants: snapshot.importedTotals
      ? snapshot.participants
      : snapshot.participants.map((participant) => ({
          ...participant,
          handicapSnapshot: {
            ...participant.handicapSnapshot!,
            agreedAt:
              participant.handicapSnapshot!.playingHandicap === null
                ? null
                : (participant.handicapSnapshot!.agreedAt ?? occurredAt),
          },
        })),
  };
}
